import { DateTime } from "luxon";
import { PAYPAL_CSV_COLUMNS } from "@/configConstants";
import {
	analyzePaymentAmount,
	calculateCostPerParticipant,
	matchPayment,
	type PayerInfo,
} from "./matching";

export type ParsedRow = Record<string, unknown>;

// PayPal payment from CSV (one entry per PayPal transaction)
export type PayPalPayment = {
	transactionCode: string;
	amountCents: number;
	type: string;
	raw: ParsedRow;
};

// Payment row (one or more per PayPal payment, displayed in grid)
export type PaymentRow = {
	id: string; // unique ID for this payment row
	paypalTransactionCode: string; // references PayPal payment
	amountCents: number;
	userId?: string | null;
	eventId?: number | string;
	costPerParticipant?: number | null;
	amountIndicator?: "info" | "warning" | null;
	checked?: boolean; // whether this row is selected for import
};

// Legacy type for backwards compatibility during transition
export type FilteredPaymentRow = {
	transactionCode: string;
	amountCents: number;
	type: string;
	raw: ParsedRow;
	userId?: string | null;
	eventId?: number | string;
	costPerParticipant?: number | null;
	amountIndicator?: "info" | "warning" | null;
};

export type FilterStats = {
	imported: number;
	duplicate: number;
	missingCode: number;
	invalidAmount: number;
	nonIncoming: number;
	typeFiltered: number;
};

export const defaultStats: FilterStats = {
	imported: 0,
	duplicate: 0,
	missingCode: 0,
	invalidAmount: 0,
	nonIncoming: 0,
	typeFiltered: 0,
};

function getValue(row: ParsedRow, key: string) {
	const match = Object.keys(row).find(
		(k) => k.trim().toLowerCase() === key.toLowerCase(),
	);
	return match ? row[match] : undefined;
}

export { getValue };

export function formatUserDisplayName(user: {
	firstname?: string;
	lastname?: string;
	displayname?: string;
}): string {
	if (user.firstname && user.lastname) {
		return `${user.firstname} ${user.lastname}`;
	}
	if (user.firstname || user.lastname) {
		return `${user.firstname || ""} ${user.lastname || ""}`.trim();
	}
	return user.displayname || "Unbekannt";
}

export function formatEventDisplayName(event: { date: string }): string {
	const eventDate = DateTime.fromISO(event.date, {
		zone: "Europe/Berlin",
	}).startOf("day");
	const today = DateTime.now().setZone("Europe/Berlin").startOf("day");
	const diffDays = Math.floor(eventDate.diff(today, "days").days);
	const weeks = Math.floor(Math.abs(diffDays) / 7);

	const formatter = new Intl.DateTimeFormat("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		timeZone: "Europe/Berlin",
	});
	const formattedDate = formatter.format(eventDate.toJSDate());

	if (diffDays === 0) {
		return `${formattedDate} (heute)`;
	}
	if (diffDays > 0 && diffDays < 7) {
		return `${formattedDate} (diese Woche)`;
	}
	if (diffDays < 0 && Math.abs(diffDays) < 7) {
		return `${formattedDate} (vor ${Math.abs(diffDays)} Tagen)`;
	}
	if (weeks === 1) {
		return `${formattedDate} (vor 1 Woche)`;
	}
	return `${formattedDate} (vor ${weeks} Wochen)`;
}

export function parseAmountToCents(value: unknown) {
	if (value === null || value === undefined) return null;
	const text = typeof value === "number" ? value.toString() : String(value);
	const normalized = text
		.replace(/\s+/g, "")
		.replace(/\./g, "")
		.replace(/,/g, ".")
		.replace(/€/g, "")
		.trim();
	const num = Number.parseFloat(normalized);
	if (Number.isNaN(num)) return null;
	return Math.round(num * 100);
}

export function filterRows(rows: ParsedRow[], importedCodes: Set<string>) {
	const seen = new Set<string>();
	const stats: FilterStats = { ...defaultStats };
	const filtered: PayPalPayment[] = [];

	rows.forEach((row) => {
		const typeValue = String(
			getValue(row, PAYPAL_CSV_COLUMNS.TYPE) ?? "",
		).toLowerCase();
		const transactionCode = String(
			getValue(row, PAYPAL_CSV_COLUMNS.TRANSACTION_CODE) ?? "",
		).trim();
		const amountCents = parseAmountToCents(
			getValue(row, PAYPAL_CSV_COLUMNS.GROSS),
		);

		if (!transactionCode) {
			stats.missingCode += 1;
			return;
		}

		if (importedCodes.has(transactionCode)) {
			stats.imported += 1;
			return;
		}

		if (seen.has(transactionCode)) {
			stats.duplicate += 1;
			return;
		}

		if (amountCents === null) {
			stats.invalidAmount += 1;
			return;
		}

		if (amountCents <= 0) {
			stats.nonIncoming += 1;
			return;
		}

		if (
			typeValue.includes("bankgutschrift") ||
			typeValue.includes("waehrungsumrechnung") ||
			typeValue.includes("währungsumrechnung") ||
			typeValue.includes("rückbuchung")
		) {
			stats.typeFiltered += 1;
			return;
		}

		seen.add(transactionCode);
		filtered.push({
			transactionCode,
			amountCents,
			type: String(getValue(row, PAYPAL_CSV_COLUMNS.TYPE) ?? ""),
			raw: row,
		});
	});

	return { filtered, stats };
}

/**
 * Creates initial payment rows from PayPal payments.
 * Each PayPal payment initially maps to one payment row.
 */
export function createInitialPaymentRows(
	paypalPayments: PayPalPayment[],
): PaymentRow[] {
	return paypalPayments.map((pp) => ({
		id: `${pp.transactionCode}-0`, // -0 indicates first/only payment
		paypalTransactionCode: pp.transactionCode,
		amountCents: pp.amountCents,
		userId: null,
		eventId: "",
		checked: true, // checked by default
	}));
}

/**
 * Applies auto-matching to filtered payment rows.
 * Matches each payment to a user and event based on the import data.
 * Returns updated rows with populated userId, eventId, and visual indicators.
 */
export function applyPaymentMatching(
	filteredRows: FilteredPaymentRow[],
	importData: {
		users: Array<{
			id: string;
			email: string;
			firstname?: string;
			lastname?: string;
			displayname?: string;
			mobile?: string;
			alternativePaypalEmails: string[];
		}>;
		events: Array<{
			id: number;
			date: string;
			total_cost_cents: number;
			participant_count: number;
			attendee_user_ids: string[];
		}>;
	},
): FilteredPaymentRow[] {
	return filteredRows.map((row) => {
		// Extract payer information from the raw CSV row
		const payerInfo: PayerInfo = {
			email: String(getValue(row.raw, PAYPAL_CSV_COLUMNS.PAYER_EMAIL) ?? ""),
			phone: getValue(row.raw, PAYPAL_CSV_COLUMNS.PHONE)
				? String(getValue(row.raw, PAYPAL_CSV_COLUMNS.PHONE))
				: undefined,
			name: String(getValue(row.raw, PAYPAL_CSV_COLUMNS.NAME) ?? ""),
			datetime: convertPayPalDatetimeToISO(
				getValue(row.raw, PAYPAL_CSV_COLUMNS.DATE),
				getValue(row.raw, PAYPAL_CSV_COLUMNS.TIME),
				getValue(row.raw, PAYPAL_CSV_COLUMNS.TIMEZONE),
			),
		};

		// Perform matching
		const matchingResult = matchPayment(
			payerInfo,
			importData.users,
			importData.events,
		);

		// Find matched event for cost calculation
		let costPerParticipant: number | null = null;
		if (matchingResult.matchedEventId !== null) {
			const matchedEvent = importData.events.find(
				(e) => e.id === matchingResult.matchedEventId,
			);
			if (matchedEvent && matchedEvent.participant_count > 0) {
				costPerParticipant = calculateCostPerParticipant(matchedEvent);
			}
		}

		// Analyze payment amount for visual indicator
		const amountIndicator = analyzePaymentAmount(
			row.amountCents,
			costPerParticipant,
		);

		return {
			...row,
			userId: matchingResult.matchedUserId,
			eventId: matchingResult.matchedEventId ?? "",
			costPerParticipant,
			amountIndicator,
		};
	});
}

/**
 * Validation result for payment import.
 */
export type ValidationResult = {
	valid: boolean;
	errors: string[];
	warnings: string[];
};

/**
 * Validates payment rows before saving.
 * Checks:
 * - All payments have users assigned
 * - If event is assigned, user attended that event
 * - No duplicate user+event combinations
 */
export function validatePaymentRows(
	paymentRows: PaymentRow[],
	importData: {
		users: Array<{
			id: string;
			firstname?: string;
			lastname?: string;
			displayname?: string;
		}>;
		events: Array<{
			id: number;
			date: string;
			attendee_user_ids: string[];
		}>;
	},
): ValidationResult {
	const errors: string[] = [];
	const warnings: string[] = [];
	const userEventPairs = new Set<string>();

	// Only validate rows that are checked (selected for import)
	const checkedRows = paymentRows.filter(
		(r) => r.checked === true || r.checked === undefined,
	);

	for (const row of checkedRows) {
		// Check: All payments must have a user assigned
		if (!row.userId) {
			errors.push(
				`Zahlung von €${(row.amountCents / 100).toFixed(2)} ist keinem Benutzer zugeordnet`,
			);
			continue;
		}

		const user = importData.users.find((u) => u.id === row.userId);
		const userName = formatUserDisplayName(user || {});

		// Check: If event is assigned and not empty, validate attendance
		if (row.eventId && row.eventId !== "") {
			const eventId =
				typeof row.eventId === "number"
					? row.eventId
					: Number.parseInt(String(row.eventId), 10);
			const event = importData.events.find((e) => e.id === eventId);
			const eventDate = event
				? formatEventDisplayName(event)
				: `Event ${eventId}`;

			if (event && !event.attendee_user_ids.includes(row.userId)) {
				warnings.push(
					`${userName} (€${(row.amountCents / 100).toFixed(2)}) war nicht als Teilnehmer bei ${eventDate} registriert. Trotzdem fortfahren?`,
				);
			}

			// Check: No duplicate user+event combinations
			const pairKey = `${row.userId}-${eventId}`;
			if (userEventPairs.has(pairKey)) {
				errors.push(
					`Doppelte Zahlung: ${userName} hat bereits eine Zahlung für ${eventDate}`,
				);
			} else {
				userEventPairs.add(pairKey);
			}
		}
	}

	return {
		valid: errors.length === 0,
		errors,
		warnings,
	};
}

/**
 * Converts PayPal CSV date, time and timezone fields to an ISO 8601 UTC string.
 * timezoneValue may be an abbreviation (CEST/CET) or an IANA zone (e.g., Europe/Berlin).
 * Falls back to Europe/Berlin when timezone is missing or unknown.
 */
export function convertPayPalDatetimeToISO(
	dateValue: unknown,
	timeValue: unknown,
	timezoneValue?: unknown,
): string {
	const dateStr = String(dateValue ?? "").trim();
	const timeStr = String(timeValue ?? "00:00:00").trim();
	const tzStr = String(timezoneValue ?? "").trim();

	// Expected date format: DD.MM.YYYY and time: HH:MM:SS (or HH:MM)
	const [d, m, y] = dateStr.split(".").map((s) => Number(s));
	const [hh = 0, mm = 0, ss = 0] = timeStr.split(":").map((s) => Number(s));

	if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
		// Fallback to now (UTC)
		return DateTime.now().toUTC().toISO();
	}

	// Map common timezone abbreviations to IANA zones
	const abbr = tzStr.toUpperCase();
	let zone = "Europe/Berlin"; // sensible default
	if (abbr === "CEST" || abbr === "CET") {
		zone = "Europe/Berlin";
	} else if (abbr === "BST" || abbr === "GMT" || abbr === "UTC") {
		// Treat GMT/UTC/BST as UTC-like / browser-local mappings — prefer explicit IANA when possible
		zone = abbr === "BST" ? "Europe/London" : "UTC";
	} else if (tzStr.includes("/")) {
		// Looks like an IANA zone (e.g., Europe/Berlin)
		zone = tzStr;
	} else if (tzStr.match(/^[+-][0-9]{2}:?[0-9]{2}$/)) {
		// Numeric offset like +0200 or +02:00 — construct a fixed-offset zone
		zone = tzStr;
	}

	const dt = DateTime.fromObject(
		{ year: y, month: m, day: d, hour: hh, minute: mm, second: ss },
		{ zone },
	);

	if (!dt.isValid) {
		// As a fallback try parsing as ISO using Luxon (rare case)
		const alt = DateTime.fromFormat(
			`${dateStr} ${timeStr}`,
			"dd.LL.yyyy HH:mm:ss",
			{ zone },
		);
		if (!alt.isValid) return DateTime.now().toUTC().toISO();
		return alt.toUTC().toISO();
	}

	return dt.toUTC().toISO();
}

/**
 * Applies auto-matching to payment rows using PayPal payment data.
 */
export function applyPaymentMatchingToRows(
	paymentRows: PaymentRow[],
	paypalPayments: Map<string, PayPalPayment>,
	importData: {
		users: Array<{
			id: string;
			email: string;
			firstname?: string;
			lastname?: string;
			displayname?: string;
			mobile?: string;
			alternativePaypalEmails: string[];
		}>;
		events: Array<{
			id: number;
			date: string;
			total_cost_cents: number;
			participant_count: number;
			attendee_user_ids: string[];
		}>;
	},
): PaymentRow[] {
	return paymentRows.map((row) => {
		const paypalPayment = paypalPayments.get(row.paypalTransactionCode);
		if (!paypalPayment) return row;

		// Extract payer information from the raw CSV row
		const payerInfo: PayerInfo = {
			email: String(
				getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.PAYER_EMAIL) ?? "",
			),
			phone: getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.PHONE)
				? String(getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.PHONE))
				: undefined,
			name: String(getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.NAME) ?? ""),
			datetime: convertPayPalDatetimeToISO(
				getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.DATE),
				getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.TIME),
				getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.TIMEZONE),
			),
		};

		// Perform matching
		const matchingResult = matchPayment(
			payerInfo,
			importData.users,
			importData.events,
		);

		// Find matched event for cost calculation
		let costPerParticipant: number | null = null;
		if (matchingResult.matchedEventId !== null) {
			const matchedEvent = importData.events.find(
				(e) => e.id === matchingResult.matchedEventId,
			);
			if (matchedEvent) {
				costPerParticipant = calculateCostPerParticipant(matchedEvent);
			}
		}

		// Analyze payment amount for visual indicator
		const amountIndicator = analyzePaymentAmount(
			row.amountCents,
			costPerParticipant,
		);

		return {
			...row,
			userId: matchingResult.matchedUserId,
			eventId: matchingResult.matchedEventId ?? "",
			costPerParticipant,
			amountIndicator,
		};
	});
}
